import {
  getCurrencyMinorUnits,
  getCurrencySymbol,
} from "@redotech/money/currencies";
import { zCurrency } from "@redotech/money/usd-amounts";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  alertDeflectionBillingLineSchema,
  alertDeflectionBillingOverviewSchema,
  automationDetailsSchema,
  billingStatsSchema,
  campaignDetailsSchema,
  disputeDetailsSchema,
  disputeOverviewSchema,
  orderTrackingOverviewSchema,
  recoverCancellationClawbackDetailsSchema,
  recoverOrderDetailsSchema,
  recoverOverviewSchema,
  recoverReturnClawbackDetailsSchema,
} from "./itemized-invoice-types";
import { SupportBaseBillingVersion } from "./team-billing";

export enum ServiceInvoiceType {
  MARKETING_SMS = "marketing-sms",
  MARKETING_EMAIL = "marketing-email",
  ORDER_TRACKING = "order-tracking",
  /** The one Support bill: platform, AI resolutions and voice as lines. */
  SUPPORT_BASE = "support-base",
  /**
   * Usage-based billing for checkout optimization.
   */
  CHECKOUT_OPTIMIZATION = "checkout-optimization",
  /**
   * Subscription billing for checkout optimization.
   */
  CHECKOUT_OPTIMIZATION_SUBSCRIPTION = "checkout-optimization-subscription",
  RETURN_AI_EXCHANGE = "return-ai-exchange",
  RETURNS = "returns",
  AEO = "aeo",
  IMS = "ims",
  RECOVER = "recover",
  DISPUTES = "disputes",
  DISPUTES_ALERTS_DEFLECTIONS = "disputes-alerts-deflections",
  LCG_COMMIT = "lcg-commit",
  /**
   * Usage-based billing for CTX (per-session, volume-tiered).
   */
  CTX_USAGE = "ctx-usage",
  /**
   * Flat monthly subscription for Dimensional Packaging (DEPO).
   */
  DIMENSIONAL_PACKAGING = "dimensional-packaging",
  WMS = "wms",
  OMS = "oms",
}

export enum PaymentStatus {
  PENDING = "pending",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

/**
 * Whether a service invoice has been successfully paid: it was billed and
 * the latest payment attempt is neither pending nor failed. Invoices without
 * a `paymentStatus` were billed through a synchronous payment method and are
 * considered paid when `billedSuccessfully` is true.
 */
export function isInvoicePaid(invoice: {
  billedSuccessfully: boolean;
  paymentStatus?: PaymentStatus | null;
}): boolean {
  return (
    invoice.billedSuccessfully &&
    invoice.paymentStatus !== PaymentStatus.PENDING &&
    invoice.paymentStatus !== PaymentStatus.FAILED
  );
}

/**
 * Whether the latest billing attempt for the invoice has hit a terminal
 * failure: either we failed to create a charge (billed successfully is false)
 * or the payment processor reported a failure (payment status is failed).
 */
export function isInvoiceFailed(invoice: {
  billedSuccessfully: boolean;
  paymentStatus?: PaymentStatus | null;
}): boolean {
  if (invoice.paymentStatus === PaymentStatus.FAILED) {
    return true;
  }
  return !invoice.billedSuccessfully && invoice.paymentStatus == null;
}

export enum BillingPeriod {
  WEEKLY = "week",
  /**
   * Twice-a-month, anchored on the team's billing day with a secondary day
   * computed via `getBimonthlySecondaryDay(anchor, daysInMonth)` (anchor + 15,
   * clamped to month length). Same semantics as the unified-billing schema's
   * `bimonthly` value and the legacy payout cycle's bimonthly schedule.
   */
  BIMONTHLY = "bimonthly",
  MONTHLY = "month",
  YEARLY = "year",
}

export enum CheckoutOptimizationBillingPeriod {
  BI_WEEKLY = "bi-weekly",
}

export const billingPeriodToFriendlyLower: Record<
  BillingPeriod | CheckoutOptimizationBillingPeriod,
  string
> = {
  [BillingPeriod.WEEKLY]: "weekly",
  [BillingPeriod.BIMONTHLY]: "bimonthly",
  [BillingPeriod.MONTHLY]: "monthly",
  [BillingPeriod.YEARLY]: "yearly",
  [CheckoutOptimizationBillingPeriod.BI_WEEKLY]: "bi-weekly",
};

export const billingPeriodToFriendlyUpper: Record<
  BillingPeriod | CheckoutOptimizationBillingPeriod,
  string
> = {
  [BillingPeriod.WEEKLY]: "Weekly",
  [BillingPeriod.BIMONTHLY]: "Bimonthly",
  [BillingPeriod.MONTHLY]: "Monthly",
  [BillingPeriod.YEARLY]: "Yearly",
  [CheckoutOptimizationBillingPeriod.BI_WEEKLY]: "Bi-weekly",
};

export function lineItemToString(lineItem: LineItem): string {
  const minorUnits = getCurrencyMinorUnits(lineItem.currency);
  const symbol = getCurrencySymbol(lineItem.currency);
  const pricePretty =
    lineItem.price?.toFixed(minorUnits) ?? (0).toFixed(minorUnits);
  if (lineItem.quantity === 1 || lineItem.quantity === null) {
    return `${lineItem.name}: ${symbol}${pricePretty}`;
  } else {
    return `${lineItem.name} \u00D7 ${lineItem.quantity}: ${symbol}${pricePretty}`;
  }
}

export type LineItem = z.infer<typeof lineItemSchema>;
export const lineItemSchema = z.object({
  name: z.string(),
  /** Price in dollars. Minor units may be longer than 2 decimal places. Null if the line
   * item is not priced.
   *
   * For example, order tracking has three separate credit counts (email, sms, and orders)
   * but only one price per billing period. The price is shown on a null-quantity line item,
   * while the quantities are shown on separate null-price line items.
   */
  price: z
    .number()
    .nullish()
    .transform((val) => val ?? null),
  currency: zCurrency,
  /** Quantity is null for line items that are not quantity-based (e.g. carrier fees) */
  quantity: z
    .number()
    .nullish()
    .transform((val) => val ?? null),
  nameDescription: z.string().optional(),
  quantityDescription: z.string().optional(),
  typeHelper: z.string().optional(),
});

export type BaseServiceInvoice = z.infer<typeof baseServiceInvoiceSchema>;
export const baseServiceInvoiceSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  lineItems: z.array(lineItemSchema),
  billingDueDate: z.date(),
  billedSuccessfully: z.boolean(),
  billingAttempts: z.number(),
  dateOfLastBillingAttempt: z.date().optional().nullable(),
  paymentStatus: createEnum(PaymentStatus, {
    enumName: "PaymentStatus",
  }).nullish(),
  failedPaymentsCount: z.number().default(0).optional(),
});

export type RecurringInvoiceData = z.infer<typeof recurringInvoiceDataSchema>;
const recurringInvoiceDataSchema = z.object({
  invoiceDateStart: z.date(),
  invoiceDateEnd: z.date(),
  periodBillingStart: z.date(),
  periodBillingEnd: z.date(),
});

export type SupportedBillingEngineInvoiceType =
  (typeof supportedBillingEngineInvoiceTypes)[number];
export const supportedBillingEngineInvoiceTypes = [
  ServiceInvoiceType.MARKETING_EMAIL,
  ServiceInvoiceType.MARKETING_SMS,
  ServiceInvoiceType.ORDER_TRACKING,
  ServiceInvoiceType.RETURN_AI_EXCHANGE,
  ServiceInvoiceType.RETURNS,
  ServiceInvoiceType.CHECKOUT_OPTIMIZATION,
  ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION,
  ServiceInvoiceType.AEO,
  ServiceInvoiceType.IMS,
  ServiceInvoiceType.RECOVER,
  ServiceInvoiceType.DISPUTES,
  ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS,
  ServiceInvoiceType.LCG_COMMIT,
  ServiceInvoiceType.CTX_USAGE,
  ServiceInvoiceType.SUPPORT_BASE,
  ServiceInvoiceType.DIMENSIONAL_PACKAGING,
  ServiceInvoiceType.WMS,
  ServiceInvoiceType.OMS,
] as const satisfies ServiceInvoiceType[];
export function isSupportedBillingEngineInvoiceType(
  invoiceType: ServiceInvoiceType,
): invoiceType is SupportedBillingEngineInvoiceType {
  return supportedBillingEngineInvoiceTypes.includes(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    invoiceType as SupportedBillingEngineInvoiceType,
  );
}

export const baseBillingEngineInvoiceSchema = baseServiceInvoiceSchema.extend({
  kind: createEnum(supportedBillingEngineInvoiceTypes, {
    enumName: "SupportedBillingEngineInvoiceType",
  }),
  recurringInvoiceData: recurringInvoiceDataSchema.optional(),
});
export type BaseBillingEngineInvoice = z.infer<
  typeof baseBillingEngineInvoiceSchema
>;

export type MarketingSmsInvoice = z.infer<typeof marketingSmsInvoiceSchema>;
export const marketingSmsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.MARKETING_SMS),
  usageBillingWindowConfigId: zExt.objectId().optional(),
  breakdowns: z
    .object({
      automations: z.array(automationDetailsSchema),
      campaigns: z.array(campaignDetailsSchema),
      inbound: billingStatsSchema.optional(),
    })
    .optional(),
});

export type MarketingEmailInvoice = z.infer<typeof marketingEmailInvoiceSchema>;
export const marketingEmailInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.MARKETING_EMAIL),
    usageBillingWindowConfigId: zExt.objectId().optional(),
    breakdowns: z
      .object({
        automations: z.array(automationDetailsSchema),
        campaigns: z.array(campaignDetailsSchema),
        inbound: billingStatsSchema.optional(),
      })
      .optional(),
  });

export type OrderTrackingInvoice = z.infer<typeof orderTrackingInvoiceSchema>;
export const orderTrackingInvoiceSchema = baseBillingEngineInvoiceSchema.extend(
  {
    kind: z.literal(ServiceInvoiceType.ORDER_TRACKING),
    usageBillingWindowConfigId: zExt.objectId().optional(),
    breakdowns: z.object({ overview: orderTrackingOverviewSchema }).optional(),
  },
);

export type SupportBaseInvoice = z.infer<typeof supportBaseInvoiceSchema>;
export const supportBaseInvoiceSchema = baseServiceInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.SUPPORT_BASE),
  refunded: z.boolean().optional(),
  recurringInvoiceData: recurringInvoiceDataSchema.optional(),
  version: createEnum(SupportBaseBillingVersion, {
    enumName: "SupportBaseBillingVersion",
  }).default(SupportBaseBillingVersion.FLAT_RATE),
});

export type CheckoutOptimizationInvoice = z.infer<
  typeof checkoutOptimizationInvoiceSchema
>;
export const checkoutOptimizationInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.CHECKOUT_OPTIMIZATION),
    ordersCreatedWithRedoShippingMethod: z.number(),
    /**
     * Subset of `billableOrdersCreated` whose triggering reason was the
     * intent/hazard system being live on the cart (no Redo shipping line and
     * no post-purchase upsell). Default 0 for invoices created before the
     * intent-billing field existed.
     */
    ordersBilledForIntent: z.number().default(0),
    billableOrdersCreated: z.number(),
    pricePerOrderInCents: z.number(),
    shopperAiPricePerOrderInCents: z.number().optional(),
    supportBasePricePerOrderInCents: z.number().optional(),
    /**
     * Pointer to the CO per-order BillingWindowConfig whose accumulated
     * counter produced this invoice's usage line item. Set by the engine
     * path; absent on legacy direct-write invoices.
     */
    usageBillingWindowConfigId: zExt.objectId().optional(),
  });

export type CheckoutOptimizationSubscriptionInvoice = z.infer<
  typeof checkoutOptimizationSubscriptionInvoiceSchema
>;
export const checkoutOptimizationSubscriptionInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION),
    usageBillingWindowConfigId: zExt.objectId().optional(),
  });

export type ReturnAiExchangeAgentInvoice = z.infer<
  typeof returnAiExchangeAgentInvoice
>;
export const returnAiExchangeAgentInvoice =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.RETURN_AI_EXCHANGE),
  });

export type ReturnsInvoice = z.infer<typeof returnsInvoiceSchema>;
export const returnsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.RETURNS),
});

export type AeoInvoice = z.infer<typeof aeoInvoiceSchema>;
export const aeoInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.AEO),
});

export type DimensionalPackagingInvoice = z.infer<
  typeof dimensionalPackagingInvoiceSchema
>;
export const dimensionalPackagingInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.DIMENSIONAL_PACKAGING),
  });

export type ImsInvoice = z.infer<typeof imsInvoiceSchema>;
export const imsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.IMS),
});

export type RecoverInvoice = z.infer<typeof recoverInvoiceSchema>;
export const recoverInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.RECOVER),
  usageBillingWindowConfigId: zExt.objectId().optional(),
  breakdowns: z
    .object({
      overview: recoverOverviewSchema,
      orders: z.array(recoverOrderDetailsSchema),
      returnClawbacks: z.array(recoverReturnClawbackDetailsSchema).optional(),
      cancellationClawbacks: z
        .array(recoverCancellationClawbackDetailsSchema)
        .optional(),
    })
    .optional(),
});

export type LcgCommitInvoice = z.infer<typeof lcgCommitInvoiceSchema>;
export const lcgCommitInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.LCG_COMMIT),
  usageBillingWindowConfigId: zExt.objectId().optional(),
  totalCommits: z.number().optional(),
  totalFeeCents: z.number().optional(),
});

export type DisputesInvoice = z.infer<typeof disputesInvoiceSchema>;
export const disputesInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.DISPUTES),
  usageBillingWindowConfigId: zExt.objectId().optional(),
  breakdowns: z
    .object({
      overview: disputeOverviewSchema,
      disputes: z.array(disputeDetailsSchema),
    })
    .optional(),
});

export type CtxUsageInvoice = z.infer<typeof ctxUsageInvoiceSchema>;
export const ctxUsageInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.CTX_USAGE),
});
export type DisputesAlertsDeflectionsInvoice = z.infer<
  typeof disputesAlertsDeflectionsInvoiceSchema
>;
export const disputesAlertsDeflectionsInvoiceSchema =
  baseBillingEngineInvoiceSchema.extend({
    kind: z.literal(ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS),
    usageBillingWindowConfigId: zExt.objectId().optional(),
    breakdowns: z
      .object({
        overview: alertDeflectionBillingOverviewSchema,
        lines: z.array(alertDeflectionBillingLineSchema),
      })
      .optional(),
  });

export type WmsInvoice = z.infer<typeof wmsInvoiceSchema>;
export const wmsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.WMS),
});

export type OmsInvoice = z.infer<typeof omsInvoiceSchema>;
export const omsInvoiceSchema = baseBillingEngineInvoiceSchema.extend({
  kind: z.literal(ServiceInvoiceType.OMS),
});

export const serviceInvoiceSchema = z.discriminatedUnion("kind", [
  marketingSmsInvoiceSchema,
  marketingEmailInvoiceSchema,
  orderTrackingInvoiceSchema,
  supportBaseInvoiceSchema,
  checkoutOptimizationInvoiceSchema,
  checkoutOptimizationSubscriptionInvoiceSchema,
  returnAiExchangeAgentInvoice,
  returnsInvoiceSchema,
  aeoInvoiceSchema,
  imsInvoiceSchema,
  recoverInvoiceSchema,
  disputesInvoiceSchema,
  disputesAlertsDeflectionsInvoiceSchema,
  lcgCommitInvoiceSchema,
  ctxUsageInvoiceSchema,
  dimensionalPackagingInvoiceSchema,
  wmsInvoiceSchema,
  omsInvoiceSchema,
]);

export type ServiceInvoice = z.infer<typeof serviceInvoiceSchema>;
